import React from "react";
import { Boat, AirplaneTilt, Truck, Anchor, MapPin, CheckCircle, Circle, Broadcast, Package } from "@phosphor-icons/react";

/**
 * LiveTracking — an animated, "live-feeling" shipment tracker used on both the public
 * site (theme="light") and the customer portal (theme="dark"). Pure SVG/CSS, driven
 * entirely by data you already have (etd/eta/sob/status/route/legs). No external service.
 */
const DAY = 86400000;
const dOnly = (s) => { if (!s) return null; const d = new Date(String(s).slice(0, 10)); return isNaN(d) ? null : d; };
const midnight = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

function relTime(s) {
  if (!s) return "";
  const then = new Date(s); if (isNaN(then)) return "";
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

const THEMES = {
  light: {
    card: "bg-white border-slate-200", track: "#e2e8f0", fill: "#2563eb", node: "#2563eb",
    text: "text-slate-900", sub: "text-slate-500", faint: "text-slate-400",
    doneText: "text-emerald-600", done: "#059669", accentBg: "bg-blue-50", accentBorder: "border-blue-100",
    barTrack: "bg-slate-100", pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  dark: {
    card: "bg-[#111318] border-[#272A35]", track: "#272A35", fill: "#F97316", node: "#F97316",
    text: "text-gray-100", sub: "text-gray-400", faint: "text-gray-600",
    doneText: "text-emerald-300", done: "#34d399", accentBg: "bg-[#0c0d12]", accentBorder: "border-[#272A35]",
    barTrack: "bg-[#0c0d12]", pill: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  },
};

// status → rough progress fraction, used only when we can't compute from ETD/ETA dates.
const STATUS_PCT = [
  [/cancel/i, 0], [/deliver|closed/i, 100], [/discharg|arriv/i, 92], [/custom/i, 95],
  [/on vessel|sail|transit|departed/i, 55], [/si |shipping instruction|docs/i, 28],
  [/allot|booking conf|booked/i, 15], [/draft|created/i, 6],
];

export default function LiveTracking({
  theme = "light", status, pol, pod, etd, eta, sob, vessel, voyage, carrier, mode, legs = [], updatedAt,
}) {
  const t = THEMES[theme] || THEMES.light;
  const isAir = /air/i.test(mode || "");
  const isRoad = /road|truck/i.test(mode || "");
  const Vehicle = isAir ? AirplaneTilt : isRoad ? Truck : Boat;
  const arrived = /deliver|discharg|closed|arrived/i.test(status || "");

  const today = midnight();
  const etdD = dOnly(etd), etaD = dOnly(eta);
  let pct;
  if (arrived) pct = 100;
  else if (etdD && etaD && etaD > etdD) {
    pct = ((today - etdD) / (etaD - etdD)) * 100;
    pct = Math.max(4, Math.min(96, pct));
  } else {
    const hit = STATUS_PCT.find(([re]) => re.test(status || ""));
    pct = hit ? hit[1] : 10;
  }

  const daysToEta = etaD ? Math.ceil((etaD - today) / DAY) : null;
  let countdown = "In transit";
  if (arrived) countdown = "Arrived";
  else if (daysToEta === null) countdown = status || "In transit";
  else if (daysToEta > 1) countdown = `Arriving in ${daysToEta} days`;
  else if (daysToEta === 1) countdown = "Arriving tomorrow";
  else if (daysToEta === 0) countdown = "Arriving today";
  else countdown = `ETA passed ${Math.abs(daysToEta)}d ago`;

  // Route nodes: POL (0%) → legs (evenly spaced) → POD (100%)
  const nodes = [{ label: pol || "Origin", sub: "POL", pctPos: 0 }];
  (legs || []).forEach((lg, i) => nodes.push({
    label: lg.port_name || "T/S", sub: "T/S", pctPos: ((i + 1) / ((legs.length || 0) + 1)) * 100,
  }));
  nodes.push({ label: pod || "Destination", sub: "POD", pctPos: 100 });

  // Milestone timeline
  const steps = [
    { key: "sob", label: "Shipped on Board", loc: pol, date: sob, icon: Anchor, done: !!sob },
    { key: "etd", label: "Departed origin", loc: pol, date: etd, icon: Vehicle, done: !!etd && etdD <= today },
    ...(legs || []).map((lg, i) => ({ key: `leg${i}`, label: `Transhipment · ${lg.port_name || ""}`, loc: lg.port_name, date: lg.etd || lg.eta, icon: MapPin, done: !!(lg.etd || lg.eta) && dOnly(lg.etd || lg.eta) <= today })),
    { key: "eta", label: arrived ? "Arrived at destination" : "ETA at destination", loc: pod, date: eta, icon: MapPin, done: arrived },
    { key: "del", label: "Delivered", loc: pod, date: null, icon: Package, done: /deliver|closed/i.test(status || "") },
  ].filter((s) => s.date || s.key === "eta" || s.key === "del");
  // current = first not-done
  const curIdx = steps.findIndex((s) => !s.done);

  const relDate = (ds) => {
    const d = dOnly(ds); if (!d) return "";
    const diff = Math.round((d - today) / DAY);
    if (diff === 0) return "today";
    if (diff === 1) return "tomorrow"; if (diff === -1) return "yesterday";
    return diff > 0 ? `in ${diff}d` : `${Math.abs(diff)}d ago`;
  };

  return (
    <div className={`rounded-xl border ${t.card} p-5`} data-testid="live-tracking">
      <style>{`
        @keyframes lt-pulse{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:1;transform:scale(1.35)}}
        @keyframes lt-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
        @keyframes lt-grow{from{width:0}}
        .lt-live{animation:lt-pulse 1.6s ease-in-out infinite}
        .lt-veh{animation:lt-bob 2.4s ease-in-out infinite}
        .lt-fill{animation:lt-grow 1.1s ease-out}
      `}</style>

      {/* Header: LIVE + status + countdown */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.pill}`}>
              <Broadcast size={11} weight="fill" className="lt-live" /> Live
            </span>
            <span className={`text-lg font-bold ${t.text}`}>{status || "In transit"}</span>
          </div>
          <div className={`text-sm mt-0.5 ${t.sub}`}>{countdown}{eta ? ` · ETA ${String(eta).slice(0, 10)}` : ""}</div>
        </div>
        {updatedAt && <div className={`text-[11px] ${t.faint}`}>updated {relTime(updatedAt)} · auto-refreshing</div>}
      </div>

      {/* Animated route */}
      <div className="relative mt-8 mb-2" style={{ height: 64 }}>
        {/* base track */}
        <div className="absolute left-0 right-0 rounded-full" style={{ top: 30, height: 4, background: t.track }} />
        {/* filled portion */}
        <div className="absolute left-0 rounded-full lt-fill" style={{ top: 30, height: 4, width: `${pct}%`, background: t.fill, transition: "width 1s ease-out" }} />
        {/* nodes */}
        {nodes.map((n, i) => (
          <div key={i} className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${n.pctPos}%`, top: 22 }}>
            <span className="w-3 h-3 rounded-full border-2" style={{ background: n.pctPos <= pct ? t.node : (theme === "dark" ? "#0c0d12" : "#fff"), borderColor: n.pctPos <= pct ? t.node : t.track }} />
            <span className={`mt-2 text-[10px] font-semibold whitespace-nowrap max-w-[90px] truncate ${n.pctPos <= pct ? t.text : t.faint}`} title={n.label}>{n.label}</span>
            <span className={`text-[8px] uppercase tracking-wider ${t.faint}`}>{n.sub}</span>
          </div>
        ))}
        {/* moving vehicle */}
        <div className="absolute lt-veh" style={{ left: `${pct}%`, top: 32, transition: "left 1s ease-out", zIndex: 2 }}>
          <span className="flex items-center justify-center rounded-full shadow" style={{ width: 30, height: 30, background: t.fill, color: "#fff", transform: "translate(-50%,-50%)" }}>
            <Vehicle size={16} weight="fill" />
          </span>
        </div>
      </div>

      {/* Progress bar + % */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className={t.sub}>{Math.round(pct)}% of voyage</span>
          <span className={t.sub}>{arrived ? "Delivered / arrived" : countdown}</span>
        </div>
        <div className={`h-2 rounded-full ${t.barTrack} overflow-hidden`}>
          <div className="h-full rounded-full lt-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${t.fill}, ${t.done})`, transition: "width 1s ease-out" }} />
        </div>
      </div>

      {/* Vessel/voyage strip */}
      {(vessel || voyage || carrier) && (
        <div className={`mt-4 p-3 rounded-lg border ${t.accentBg} ${t.accentBorder} flex flex-wrap gap-x-6 gap-y-1 text-sm`}>
          {vessel && <div><span className={`text-[10px] uppercase tracking-wider ${t.sub}`}>Vessel </span><span className={`font-semibold ${t.text}`}>{vessel}</span></div>}
          {voyage && <div><span className={`text-[10px] uppercase tracking-wider ${t.sub}`}>Voyage </span><span className={`font-semibold ${t.text}`}>{voyage}</span></div>}
          {carrier && <div><span className={`text-[10px] uppercase tracking-wider ${t.sub}`}>Carrier </span><span className={`font-semibold ${t.text}`}>{carrier}</span></div>}
          {mode && <div><span className={`text-[10px] uppercase tracking-wider ${t.sub}`}>Mode </span><span className={`font-semibold ${t.text}`}>{mode}</span></div>}
        </div>
      )}

      {/* Milestone timeline */}
      <div className="mt-5">
        <ol className="relative border-l-2 ml-2" style={{ borderColor: t.track }}>
          {steps.map((s, i) => {
            const Icon = s.icon || Circle;
            const isCur = i === curIdx;
            return (
              <li key={s.key} className="ml-4 pb-4 last:pb-0">
                <span className="absolute -left-[11px] flex items-center justify-center rounded-full" style={{ width: 18, height: 18, background: s.done ? t.done : (isCur ? t.fill : (theme === "dark" ? "#0c0d12" : "#fff")), border: `2px solid ${s.done ? t.done : (isCur ? t.fill : t.track)}` }}>
                  {s.done ? <CheckCircle size={11} weight="fill" color="#fff" /> : (isCur ? <Icon size={10} weight="fill" color="#fff" /> : null)}
                </span>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm ${s.done ? t.doneText : (isCur ? t.text + " font-semibold" : t.faint)}`}>{s.label}{isCur && <span className="lt-live ml-1.5 inline-block w-1.5 h-1.5 rounded-full align-middle" style={{ background: t.fill }} />}</span>
                  <span className={`text-[11px] ${t.faint}`}>{s.date ? `${String(s.date).slice(0, 10)} · ${relDate(s.date)}` : ""}</span>
                </div>
                {s.loc && <div className={`text-[11px] ${t.sub}`}>{s.loc}</div>}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
